/* eslint-disable @nice-cxone/ccf/required-tsdoc */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import {
  API_BASE_URL,
  ApiResponse,
  callApi,
  PROCESS_UTTERANCE,
} from '../../ccf-app-space/ccf-copilot/ccf-copilot-api';
import { processApiResponse } from '../../ccf-app-space/ccf-copilot/ccf-copilot-util';
import {
  setAudioTranscript,
} from '../../ccf-app-space/ccf-copilot/ccf-copilot-chat/+state/ccf-live-copilot.slice';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { v5 as uuidv5, NIL as namespaceNIL } from 'uuid';
import { activeContactSelector } from '../ccf-inbox.slice';
import { CcfBox, CcfTypography } from '@nice-ccf/ui-controls';
import { LiveVoiceWSClient } from '@nice-ccf/core-sdk';
import { notification } from 'antd';

/* eslint-disable-next-line */
export interface CcfCopilotHcaProps {}

const WEBSOCKET_URL = 'wss://zesrxbvpy8.execute-api.us-west-2.amazonaws.com/dev';
const UTTERANCE_MATCH_THRESHOLD = 81;

/**
 * Component to display assignment box on UI
 * @example - <CcfCopilotHca/>
 */
export function CcfCopilotHca(props?: CcfCopilotHcaProps) {
  const dispatch = useDispatch();
  const activeContact = useSelector(activeContactSelector);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [isRecording, setIsRecording] = useState(true);
  // const copilotConfigurations = useSelector(getCopilotConfig);
  const copilotConfigurations = JSON.parse(localStorage.getItem('agentAssistAppConfig') as string) || {};
  const [transcript, setTranscript] = useState<string>('');
  const namespace = uuidv5('namespace', namespaceNIL);
  const [error, setError] = useState<string | null>(null);
  const intents = activeContact?.intents ?? [];
  const customReplies = activeContact?.customReplies ?? [];
  const customerSentiment = activeContact?.customerSentiment ?? [];
  const confidentUtterance = activeContact?.confidence ?? [];
  const languageCode =
    copilotConfigurations?.language?.toLowerCase() ?? 'en-us';
  const acpWsClient = useRef<LiveVoiceWSClient | null>(null);
  const deepgramSocketRef = useRef<WebSocket | null>(null);

  const [connectionStatus, setConnectionStatus] =
    useState<string>('Disconnected');

  const requestBody = {
    contactId: activeContact?.id,
    busNo: 12345678,
    tenantId: '12345678',
    participantId: '',
    messageBody: '',
    getSalesOffers: false,
    getCompliance: false,
    getKbCombo: false,
    getProcessSteps: false,
    getConversationAssistance: false,
    messageMetaData: {
      completeUtterance: true,
    },
    transcriptionEventId: '',
    threshold_sales: copilotConfigurations?.threshold_sales ?? 0.75,
    threshold_compliance: copilotConfigurations?.threshold_compliance ?? 0.75,
    agentAssistAppConfig: {
      language: languageCode,
      generativeResponseTone:
        copilotConfigurations?.generativeResponseTone ?? 'Formal',
      channels: ['digital', 'voice'],
      transcriptionProfileName: 'google-test',
      enableSentiment: copilotConfigurations?.enableSentiment ?? true,
      sentimentResponseLength:
        copilotConfigurations?.sentimentResponseLength ?? 20,
      realTimeSummary: copilotConfigurations?.realTimeSummary ?? true,
      realTimeSummaryResponseLength:
        copilotConfigurations?.realTimeSummaryResponseLength ?? 25,
      finalSummary: copilotConfigurations?.finalSummary ?? true,
      finalSummaryResponseLength:
        copilotConfigurations?.finalSummaryResponseLength ?? 65,
      behavioralGuidance: true,
      behavioralGuidanceResponseLength: 10,
      behevioralGuidanceNumberOfResponse: 3,
      enableExpertCopilotIntegration: true,
      expertCopilotURL: copilotConfigurations?.expertCopilotURL ?? '',
      expertCopilotClientKey:
        copilotConfigurations?.expertCopilotClientKey ?? '',
      expertCopilotClientSecret:
        copilotConfigurations?.expertCopilotClientSecret ?? '',
      path: copilotConfigurations?.path ?? '',
      botPersona: copilotConfigurations?.botPersona ?? '',
      kBAnswersPath: copilotConfigurations?.kBAnswersPath ?? '',
      salesOffersPath: copilotConfigurations?.salesOffersPath ?? '',
      compliancePath: copilotConfigurations?.compliancePath ?? '',
      nextBestResponse: copilotConfigurations?.nextBestResponse ?? true,
      nextBestResponseLength:
        copilotConfigurations?.nextBestResponseLength ?? 20,
      nextBestNumberOfResponse:
        copilotConfigurations?.nextBestNumberOfResponse ?? 2,
      knowledgeBaseArticles:
        copilotConfigurations?.knowledgeBaseArticles ?? true,
      knowledgeBaseArticlesResponseLength:
        copilotConfigurations?.knowledgeBaseArticlesResponseLength ?? 2,
      knowledgeBaseWebLinks:
        copilotConfigurations?.knowledgeBaseWebLinks ?? true,
      knowledgeBaseMaximumWebLinks:
        copilotConfigurations?.knowledgeBaseMaximumWebLinks ?? 3,
      knowledgeBaseImages: copilotConfigurations?.knowledgeBaseImages ?? true,
      knowledgeBaseMaximumImages:
        copilotConfigurations?.knowledgeBaseMaximumImages ?? 3,
      knowledgeBaseProcessSteps:
        copilotConfigurations?.knowledgeBaseProcessSteps ?? true,
      knowledgeBaseMaximumSteps:
        copilotConfigurations?.knowledgeBaseMaximumSteps ?? 10,
      threshold: copilotConfigurations?.threshold ?? 0.75,
      vendorId: 'agentCopilot',
      agentAssistId: 'Ved_acp_test123',
      scriptParams: '',
      backAndForthLiveConvo:
        copilotConfigurations?.backAndForthLiveConvo ?? false,
      backAndForthLiveConvoLength:
        copilotConfigurations?.backAndForthLiveConvoLength ?? 4,
    },
    messageIsCompletedUtterance: true,
    agentId: 1898,
    mediaType: activeContact?.media === 'Voice' ? 'Voice' : 'Digital',
    totalAudioChunksInMilliSeconds: 2040,
  };

  const impersonatePatron = !requestBody.agentAssistAppConfig.backAndForthLiveConvo && requestBody.agentAssistAppConfig.path === 'marriott';

    const calculateStringMatchPercentage = (str1: string, str2: string): number => {
        // Tokenize and normalize strings (convert to lowercase)
        const words1 = str1.toLowerCase().split(/\W+/).filter(Boolean);
        const words2 = str2.toLowerCase().split(/\W+/).filter(Boolean);

        // Determine which string has fewer words for baseline comparison
        const shorterWords = words1.length < words2.length ? words1 : words2;
        const longerWords = words1.length >= words2.length ? words1 : words2;

        // Create a set from the longer string's words for faster lookup
        const longerSet = new Set(longerWords);

        // Count matching words from the shorter string in the longer string
        let matchCount = 0;
        for (const word of shorterWords) {
            if (longerSet.has(word)) {
                matchCount++;
            }
        }

        // Calculate the match percentage based on the shorter string length
        const matchPercentage = (matchCount / shorterWords.length) * 100;

        return Math.min(matchPercentage, 100); // Ensure the match % does not exceed 100
    }


  /**
   * Function to handle WebSocket message
   * @example - handleAcpWsMessages(event)
   * @returns
   */
  const handleAcpWsMessages = (event: any) => {
    try {
      if (event) {
        console.log('WebSocket message:', event);
        // const message = event.data;
        // if (message && Array.isArray(message.transcriptionResponse)) {
        //   const audioTranscriptText = message.transcriptionResponse.join(' ');
        //   console.log('Joined transcription text:', audioTranscriptText);

        //   if (audioTranscriptText) {
        //     dispatch(setAudioTranscript(audioTranscriptText));
        //     setTimeout(() => {
        //       dispatch(setAudioTranscript(''));
        //     }, 5000);

        //     const customerRequestBody = {
        //       ...requestBody,
        //       messageBody: audioTranscriptText,
        //       participantId: 'agent',
        //       transcriptionEventId: uuidv5(audioTranscriptText, namespace),
        //     };
        //     // Send transcription result to process-utterance API
        //     // sendToProcessUtteranceAPI(customerRequestBody, audioTranscriptText);
        //   }
        // }
        if (event?.msg === 'Ok' && event?.connectionId) {
          console.log('Agent connection established', event?.connectionId);
          callApi(
            API_BASE_URL + '/agent-assist-connection',
            'POST',
            {
                ...requestBody,
                messageBody: 'Agent connection established',
                participantId: 'agent',
                transcriptionEventId: uuidv5('Agent connection established', namespace),
            //   contactId: activeContact?.id,
              connectionId: event?.connectionId,
            //   config: copilotConfigurations,
            },
            dispatch
          );
        }
        if (
          event?.type === 'customerUtterance' &&
          event?.utterance?.length > 0
        ) {
            const customerUtterance = event?.utterance || '';
          console.log('customerUtterance message:', customerUtterance);
          if(customerUtterance.includes('patron:')) {
            dispatch(setAudioTranscript(customerUtterance));
            setTimeout(() => {
                dispatch(setAudioTranscript(''));
            }, 5000);

            const cleanedUtterance = customerUtterance.replace(/patron:/gi, '').trim();
            console.log('cleanedUtterance:', cleanedUtterance);
            if(intents.length > 0){
                handleIntent(cleanedUtterance);
              }
              if(customReplies.length > 0){
                handleBehavioralGuidance(cleanedUtterance);
              }
              if(customerSentiment.length> 0){
                handleCustomerSentiment(cleanedUtterance);
              }
          }
        }
        if (event?.data) {
            if( confidentUtterance.length > 0){
                const userReply = JSON.stringify(event?.data);
                if(userReply.includes('kbCombo') && event?.originalUtterance){
                    const matchingConfidence = confidentUtterance?.find((item: any) => {
                        const matchPercentage = calculateStringMatchPercentage(event?.originalUtterance, item.statement);
                            // console.group('GreenDot Confidence Match');
                            // console.log('%c statement ', 'color: red', item.statement);
                            // console.log('%c OG utterance ', 'color: blue', event?.originalUtterance);
                            // console.log('match % -->', matchPercentage);
                            // console.groupEnd();

                            return matchPercentage >= UTTERANCE_MATCH_THRESHOLD;
                            // return item.statement.toLowerCase() === event?.originalUtterance.toLowerCase();
                        });

                    const showGreenConfidenceDot = matchingConfidence ? matchingConfidence.value === 'High' : false;
                    processApiResponse(event?.data, dispatch, 'Digital', false, false, showGreenConfidenceDot);
                }
                else{
                    processApiResponse(event?.data, dispatch, 'Digital', false);
                }
            }
            else{
              let isProcessStepsAvailable = false;
              const parsedContent = JSON.parse(event?.data?.acpAppElements[0]?.content);
              if (parsedContent.kbProcessSteps && parsedContent.kbProcessSteps.length > 0) {
                isProcessStepsAvailable = true;
              }
              processApiResponse(event?.data, dispatch, 'Digital', isProcessStepsAvailable);
            }
        } else {
          console.warn('Received invalid WebSocket message');
        }
      } else {
        console.warn('Received empty WebSocket message');
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  };

    /**
     * Formats the copilot reply into a behavioral guidance card content.
     *
     * This function takes a copilot reply string and determines the appropriate title and icon
     * based on the content of the reply. It then returns an object containing the title, icon,
     * and a cleaned version of the copilot reply.
     *
     * @param copilotReply - The reply from the copilot which needs to be formatted.
     * @returns  An object containing the title, icon, and cleaned copilot reply.
     *
     * @example
     * const reply = "Alert: This is an alert message.";
     * const formattedContent = formatToBehaviouralgudanceCardContent(reply);
     */
    let title, icon;
    const formatToBehaviouralgudanceCardContent = (copilotReply: string) => {
        if (copilotReply.includes('Alert')) {
            title = 'Copilot Alert';
            icon =
                'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAgAAAAIACAMAAADDpiTIAAAAA3NCSVQICAjb4U/gAAAACXBIWXMAABjGAAAYxgH2cLukAAAAGXRFWHRTb2Z0d2FyZQB3d3cuaW5rc2NhcGUub3Jnm+48GgAAAvRQTFRF////4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldM4ldMf/NSnwAAAPt0Uk5TAAECAwQFBgcICQoLDA0ODxAREhMUFRYXGBkaGxweHyAhIiMkJSYnKCkqKywtLi8wMTIzNDU2Nzg5Ojs8PT4/QEFCQ0RFRkdISUpLTE1OT1BRUlNUVVZXWFlaW1xdXl9gYWJjZGVmZ2hpamtsbW5vcHFyc3R1dnd4eXp7fH5/gIGCg4SFhoeIiYqLjI2Oj5CRkpOUlZaXmJmam5ydnp+hoqOkpaanqKmqq6ytrq+wsbKztLW2t7i5uru8vb6/wMHCw8TGx8jJysvMzc7P0NHS09TV1tfY2drb3N3e3+Dh4uPk5ebn6Onq6+zt7u/w8fLz9PX29/j5+vv8/f4ellHCAAAhi0lEQVQYGe3Be2CV5Z0n8G/OOQmEXIw1LDhAmgV3pK7yCkP7WhRCRkGnFmYUdFKWhNqYMc4qyOB0R8KIY1KvoOsgRQqFETNFAruwMsiKSl25RB1Qh0kJEhGTwHAJUU8uJDnff7bUWkFu+T3Pe855nnPezwfJI33wiMKp98xdsGLNhlff3Pbuh/UHDrWEu7vDLYcO1H/47rY3X92wZsWCufdMLRwxOB2+RJE69MbSylVbdh1so0DbwV1bVlWW3jg0DT47BfLGzXh4xdZPeqil5+CvV87/ccG3A/BZ47Lx9/9iZ5ieaqtdNrMwFz6zpY2Y/sSmJkZN06Ynp49Ig89EQ4tf+KCLMdD1wQvFQ+EzSGj0rDXNjKnmNbNGh+CLv+yJj7weZlyEX39kYjZ88RNw5+/oYVz17JjvBuCLg4El1cdohGPVJQPhi6XQ2Kr3IjRI5L2qsSH4YuKSkppWGqi1puQS+KIsa9r6Thqrc/20LPiiJuPOte00XPvaOzPgi4L021eHaYXw6tvT4fNU4OaXPqdFPn/p5gB8Xhk87wCtc2DeYPg8EJy0oZtW6t4wKQifnvxHG2mxxkfz4VOWOmVzhJaLbJ6SCp+K3HmHmRAOz8uFT2rY821MGG2Lr4BP4vtre5hQetZdD18vBW57mwlo+5QgfBfX7956Jqj992XAd2GZDx1lAjs+Lxu+8+s35wgT3LGfZsB3bn3ub2YSOPxAX/jOllb+KZNE471p8J0p9JOPmUQOlIbg+1pg+j4mmX3TA/D9XsEuJqFdBfCdkl/DJFWTD19mVQeTVkdVJpJbyoxmJrXmGSlIYmNqmfRqxyBZDamm77eqhyAZBeeE6fud8Jwgko7zDn1/8I6D5NL3sS76TtP1WF8kkYK99H3D3gIki5ylEfrOElmag6RwWxN959R0GxLfwLX0ndfagUhwP/wP+i7gP36IRJb+PH0X8Xw6EtaIPfRd1J4RSEwpszvp64XO2SlIQJdvZpxFjtdvf2XlworyOyaOu27kVcOGDMjpFwz2yxkwZNhVI68bN/GO8oqFK1/ZXn88wjjbfDkSzqQjjJeuj15b+lCROyCIXgoOcIseWvraR12MlyOTkFjSFzMemjc/dVdBXhCKgnkFdz21uZnxsDgdCWTobsZYeOeymYX94Yn+hTOX7QwzxnYPRcL4QQtjqOf9xdOuCMBjgSumLX6/hzHU8gMkhsD8CGOlbWvlLTmImpxbKre2MVYi8wNIAJduZGy0bZzjpiLqUt05G9sYGxsvhfWc/YyFPQsm9EXM9J2wYA9jYb8DyxW3Mepa15XlIeaGlK1rZdS1FcNmaYsYbY3PjEtFnKSOe6aR0bYoDdYa8Daj69CisQHEVWDsokOMrrcHwFLDGxhNR5YUBmGAYOGSI4ymhuGw0vgWRs8XyyeEYIzQhOVfMHpaxsNCxScZNbVlWTBMVlkto+ZkMazzMKPlxCIHRnIWnWC0PAy7pK1klLxVnA5jpRe/xShZmQaL5LzBqOhcMhyGG76kk1HxRg6skb+H0RBeOAgWGLQwzGjYkw9LjDrMKGit6g9L9K9qZRQcHgUrXN9K7x2tyIFFciqO0nut18MCN4XpuabZGbBMxuwmei58E4w3uYNeayjvAwv1KW+g1zomw3BFXfRYXUkIlgqV1NFjXUUwWmkPvbVragAWC0zdRW/1lMJgsyL01LZbYb1bt9FTkVkwVgU91TAJCWFSAz1VAUM9Ti91VqYjQaRXdtJLj8NIj9NLW65EArlyC730OAxUQQ81FyHBFDXTQxUwzix6p/vZbCSc7Ge76Z1ZMExphJ7Zfi0S0rXb6ZlIKYxS1EOvHLs7BQkq5e5j9EpPEQwyuYseiSzLRQLLXRahR7omwxg3ddAju8cgwY3ZTY903ARDXB+mNz6bHULCC83+jN4IXw8jjGqlN9YPQlIYtJ7eaB0FA+QfpidOzkLSmHWSnjicj7jL2UNP7B+NJDJ6Pz2xJwdxlvYGPVGTg6SSU0NPvJGG+FpJL3Teh6RzXye9sBJx9TC9sG8UktCoffTCw4ijYnphdTaSUvZqeqEYcTP+JPV1lCNplXdQ38nxiJPhLdS310ESc/ZSX8twxMWABuqrzkJSy6qmvoYBiIO0t6mtvQxJr6yd2t5OQ+wtora6a+DDNXXU9jxirpjaXs6E77cyX6a2GYgxp426ngvA9zuB56irfSRi6tL91DUXvj+YS10fX4YYCmykpu5S+E5T2k1NrwYQO/OpqX0yfGeY3E5NVYiZH0Sop+UG+L7hhhbqifw5YmRoC/U0Xg3fWa5upJ7WP0ZMpO+mnro8+M4hr4569mQiFhZTz45c+M4pdwf1/BIxMIl6NmXAdx4Zm6hnKqLu8iPU8mIqfOeV+iK1HB+MKEvZTC1Pp8B3ASlPU8vrAUTXbOqIPAjfRTwYoY6/RVSN6KSGrhL4LqqkixpOjkQUpe+hhkgJfL1QEqGGun6Inuep40H4euVB6vg5ouaH1PE0fL30NHVMQpQM/A9qeDEFJrl84o/+uuLp5evWLX+64q+LbvwWTJLyIjUcGYjoWEsNm1JhiksK/8fagzxTpO6XZdcEYIrUTdSwFlFxGzXsyIAZUv/ilW6ex/F/HAFDZOyghtsQBTlNVFeXCyNc+cRhXtDOu7NghNw6qmvKgfeWUl1jHkzwnzfw4j7/h3SYIK+R6pbCcwURKmu5GgboM6+dvfLRn8EEV7dQWaQAHuu7l8rab4ABbq5nr9UMhgFuaKeyvX3hrceorHsyDDCfEp/dCANM7qayx+App4vKShF/Kf9Imc4pMEAplXU58FDwHSqbi/gLraJUz1/BAHOp7J0gvDOHyp5D/AXWU8FPYYDnqGwOPDMkTFUvBxB/D1JFZALiL/AyVYWHwCvVVFWXifj7TgeVfHop4i+zjqqq4ZExVNV+DeIv9A4V/TMMcE07VY2BJ1JqqaoMBphHZUUwQBlV1abACzOoqhoGSG+lsoMBGKCaqmbAA5nNVLQ3CwYoooaJMEDWXipqzoS+KirqcGCCTdTwzzCB00FFVdCW30FF5TDB5d3U0HEpTFBORR350FVDRathhL+hlnthhNVUVANNBVS0LxtGeJFa/glGyN5HRQXQEthFNZ2jYIZfU8uvYYZRnVSzKwAd06noPhiigVoOwBD3UdF0aAjto5oaGCLlJLV0h2CIGqrZF4K6n1DN/hwY4nJqyoMhcvZTzU+gLO1jKjk5Gqa4hpq+C1OMPkklH6dBVTnVzIIxHGpyYYxZVFMORX0+pZL1MIdDTS7MsZ5KPu0DNfdTyWeDYA6HmlyYY9BnVHI/lPRrppLZMIhDTS4MMptKmvtBxRwq2R2CQRxqcmGQ0G4qmQMFmUeoIjIGJnGoyYVJxkSo4kgm5B6ikmUwikNNLoyyjEoegli/o1RxLBdGcajJhVFyj1HF0X6QupdK7oZZHGpyYZa7qeReCAXqqWJ7CsziUJMLs6Rsp4r6AGRuo4rua2EYh5pcGObabqq4DTJvU8WzMI1DTS5M8yxVvA2R71NFczZM41CTC9NkN1PF9yGxliqKYByHmlwYp4gq1kJgWA8VbIF5HGpyYZ4tVNAzDL33PBV0XgnzONTkwjxXdlLB8+i13DYqqISBHGpyYaBKKmjLRW/No4KGdBjIoSYXBkpvoIJ56KXUw1QwCSZyqMmFiSZRweFU9M4UKtgGIznU5MJI26hgCnpnMxXcCiM51OTCSLdSwWb0Sn6EcrtgJoeaXJhpF+Ui+eiNR6lgKszkUJMLM02lgkfRC8FGytUFYCaHmlyYKVBHucYgLm4SFZTAUA41uTBUCRVMwsVtoFxDCIZyqMmFoUINlNuAixrcTblymMqhJhemKqdc92BczDzKNfWBqRxqcmGqPk2Um4eLCByg3GwYy6EmF8aaTbkDAVzYzZQ7mgFjOdTkwlgZRyl3My7sJcpVwFwONbkwVwXlXsIFpX9OsdYcmMuhJhfmymml2OfpuJDbKVcFgznU5MJgVZS7HReymmLh/jCYQ00uDNY/TLHVuICMMMUWwmQONbkw2UKKhTNwfndSrHMQTOZQkwuTDeqk2J04v7UUWwKjOdTkwmhLKLYW55XVTrHhMJpDTS6MNpxi7Vk4n2kUewtmc6jJhdneotg0nM96ihXDbA41uTBbMcXW4zwu6aTUiXSYzaEmF2ZLP0GpzktwbiUUWwTDOdTkwnCLKFaCc6uhmAPDOdTkwnAOxWpwTqFWStXCdA41uTBdLaVaQziXsRQrg+kcanJhujKKjcW5VFHqiyyYzqEmF6bL+oJSVTiX9yi1HMZzqMmF8ZZT6j2cw8AIpSbAeA41uTDeBEpFBuJsJZQ6EoLxHGpyYbzQEUqV4GzVlFoC8znU5MJ8SyhVjbMEjlGqEOZzqMmF+QopdSyAb3IpdSgI8znU5MJ8wUOUcvFN8ym1CBZwqMmFBRZRaj6+aQelxsICDjW5sMBYSu3AN2T3UKgxAAs41OTCAoFGCvVk40wTKfUMbOBQkwsbPEOpiTjTI5QaBxs41OTCBuMo9QjO9DqFWlNhA4eaXNggtZVCr+MMoTCF1sEKDjW5sMI6CoVDON1oSpXBCg41ubBCGaVG43SzKJUHKzjU5MIKQyg1C6dbQ6E9sINDTS7ssIdCa3C6ZgotgB0canJhhwUUasZphlJqAuzgUJMLO0yg1FB8rZhCbX1hB4eaXNihbxuFivG1Fyi0EZZwqMmFJTZS6AV87QMKzYElHGpyYYk5FPoAf5DWRSEXlnCoyYUlXAp1peErIyjUlgpLONTkwhKpbRQaga9Mp9BW2MKhJhe22Eqh6fjKExSqhC0canJhi0oKPYGvbKLQLbCFQ00ubHELhTbhK02U6cmBLRxqcmGLnB7KNOH3LqPQ+7CGQ00urPE+hS7Dl8ZTaDGs4VCTC2ssptB4fOl+Ck2DNRxqcmGNaRS6H1/6BYWugDUcanJhjSso9At8aSdlwgFYw6EmF9YIhCmzE78TCFNmJ+zhUJMLe+ykTDiAU/IotAz2cKjJhT2WUSgPp4yj0EzYw6EmF/aYSaFxOGUGhQphD4eaXNijkEIzcMrDFOoPezjU5MIe/Sn0ME5ZQZlmWMShJhcWaabMCpyylTKbYRGHmlxYZDNltuKUTyjzFCziUJMLizxFmU/wW6k9lLkLFnGoyYVF7qJMTyqAoRQqgEUcanJhkQIKDQVwI4XyYBGHmlxYJI9CNwIopUxXEBZxqMmFRYJdlCkFUEmZj2ATh5pc2OQjylQCWEWZ12ATh5pc2OQ1yqwCsIUyS2ETh5pc2GQpZbYA2EWZh2ATh5pc2OQhyuwCcJAyRbCJQ00ubFJEmYMA2ijjwiYONbmwiUuZNiCdQgNgE4eaXNhkAIXSMZgykSBs4lCTC5sEI5QZjBGUOQ6rONTkwirHKTMChZSph1UcanJhlXrKFGIqZbbDKg41ubDKdspMxT2UeQVWcajJhVVeocw9mEuZlbCKQ00urLKSMnOxgDILYRWHmlxYZSFlFmAFZSpgFYeaXFilgjIrsIYy5bCKQ00urFJOmTXYQJk7YBWHmlxY5Q7KbMCrlJkIqzjU5MIqEynzKt6kzDhYxaEmF1YZR5k3sY0y18EqDjW5sMp1lNmGdykzElZxqMmFVUZS5l18SJmrYBWHmlxY5SrKfIh6ygyDVRxqcmGVYZSpxwHKDIFVHGpyYZUhlDmAQ5QZAKs41OTCKgMocwgtlMmBVRxqcmGVHMq0IEyZfrCKQ00urNKPMmF0UyYIqzjU5MIqQcp0o5syQVjFoSYXVglSphthyvSDVRxqcmGVfpQJo4UyObCKQ00urJJDmRYcoswAWMWhJhdWGUCZQzhAmSGwikNNLqwyhDIHUE+ZYbCKQ00urDKMMvX4kDJXwSoONbmwylWU+RDvUmYkrOJQkwurjKTMu9hGmetgFYeaXFjlOspsw5uUGQerONTkwirjKPMmXqXMRFjFoSYXVplImVexgTJ3wCoONbmwyh2U2YA1lCmHVRxqcmGVcsqswQrKVMAqDjW5sEoFZVZgAWUWwioONbmwykLKLMBcyqyEVRxqcmGVlZSZi3so8wqs4lCTC6u8Qpl7MJUy22EVh5pcWGU7ZaaikDL1sIpDTS6sUk+ZQoygzHFYxaEmF1Y5TpkRGEyZSBA2cajJhU2CEcoMRjqFBsAmDjW5sMkACqUDbZRxYROHmlzYxKVMG4CDlCmCTYZS03dgkyLKHASwizIPwSZ9ItSTAZs8RJldALZQZimscphajsIqSymzBcAqyrwGq7xDLe/CKq9RZhWASsp8BKuspZa1sMpHlKkEUEqZriBs8iS1VMImwS7KlAK4kUJ5sMn3qOUq2CSPQjcCGEqhAljlN9SwE1YpoNBQAGk9lLkLVplLDffAKndRpicNv3WQMk/BKnkRKmvPgVWeosxBnPJrymyGXTZS2f+EXTZT5tc4ZSVlmmGXvM+oaG8/2KWZMitxynwK9YddSqmm+3uwS38KzccpP6ZQISyzkUoegWUKKfRjnFJAoZmwzB8dpYK3QrDMTAoV4JRvU2gZbHNVI8U2Z8A2yyj0bZwSaKPMTlgnfx+FXk6DdXZSpi2A36mlTDgA6wzcTZGlAVgnEKZMLb60jEJXwD451ey9E/8dFrqCQsvwpZkUmgYbjf839tKLA2GjaRSaiS8VUmgxrBT6m8/YC3sKYKfFFCrEl3Ip9D4sNfDvP+aFRbb8ZSos9T6FcvF7TZTpyYGtAhN+1cnzav7ZUFgrp4cyTfjKJgrdAovl/uSFXV08S+trj94SgsVuodAmfOVJClXCcv3GPPDL9W992NhGfvHJv255+R/v+k4Alquk0JP4ynQKbUWi6NMHiWIrhabjKyMo1JYKn2FS2yg0Al9J66KQC59hXAp1peEPPqDQHPgMM4dCH+BrL1BoI3yG2UihF/C1Ygq19YXPKH3bKFSMrw2l1AT4jDKBUkNxmmYKLYDPKAso1IzTraHQHviMsodCa3C6WZTKg88gQyj1AE43mlJl8BmkjFLfxelCYQqtg88g6ygUTsUZXqdQayp8xkhtpdAbONMjlBoHnzHGUepRnGkipZ6BzxjPUOpmnCm7h0KNAfgMEWikUM8l+IYdlBoLnyHGUqoW3zSfUovgM8QiSv0Dvsml1KEgfEYIHqLU9/FNgWOUKoTPCIWUOh7EWaoptQQ+Iyyh1K9wthJKHQnBZ4DQEUrNwNkGRig1AT4DTKDY5TiH9yi1HD4DLKfUv+Jcqij1RRZ8cZf1BaV+hnMZS7Ey+OKujGJjcS6hVkrVwhd3tZRqDeGcaijmwBdnDsVqcG4lFFsEX5wtolgJzu2STkqdSIcvrtJPUKrzEpzHeooVwxdXxRRbj/OZRrG34Iurtyg2DeeT1U6x4bBTv1tnPv5PW+o++/w3b6x6YtakTNhpOMXas3Beaym2BBa6bMb/auMZOl75q8thoSUUW4vzu5NinYNgmdSyN7t5DpEdD/SFZQZ1UuxOnF9GmGILYZWUO+p5Xp+UBGCVhRQLZ+ACVlMs3B8WGV/LC9p9MyzSP0yx1biQ2ylXBWsM3MiL+r+DYY0qyt2OC0n/nGKtObDEyIPshebvwRI5rRT7PB0X9BLlKmCHO9rYK+3/DXaooNxLuLCbKXc0AxZImR9hb/0sAAtkHKXczbiwwAHKzYYFVlLgVykw32zKHQjgIuZRrqkPjDePIlUwXp8mys3DxQzuplw5THd7hDI/gunKKdc9GBe1gXINIZjt2jCF2r8Ls4UaKLcBFzeJCkpgtAGfUKxpEIxWQgWTcHHBRsrVBWCydVTwLzBZoI5yjUH0wqNUMBUG+z6VFMJgU6ngUfRGfoRyu2Cw/0cltSkw1y7KRfLRK5up4FYYazIV3Qlj3UoFm9E7U6hgG0wV3ENF+1Jhqm1UMAW9k3qYCibBUNOp7G4YahIVHE5FL82jgoZ0mOkVKtsCM6U3UME89FZuGxVUwkiZHVTWdSmMVEkFbbnotcVU0HklTDSVGqbDRFd2UsFi9N4VPVSwBSZaRQ01MNEWKui5AgLrqKII5kk9QQ1f9IV5iqhiHSSup4rmbBhnHLVMhHGym6nieohsp4pnYZwfU8s9MM6zVLEdMlOoovtamGYutTwM01zbTRVTIBPcTxXbU2CYRdSyBIZJ2U4V+4MQuo9K7oZh1lHLehjmbiq5D1IZx6niWC7MsoNaamGW3GNUcTwDYvOoZBnMcpBaPoVZllHJPMhlH6OKyBgY5RNq+QRGGROhimPZUPBTKtkdgkm2U8sOmCS0m0p+ChUZh6lkNkxSQy3/GyaZTSWHM6DkASr5bBAM8hy1/BwGGfQZlTwANX0bqWQ9DPJ31PL3MMh6KmnsC0X3Us0smKOEWspgjllUcy9UpR2gkpOjYYwx1PKnMMbok1RyIA3KSqlmfw5METxKDa1pMEXOfqophbrQPqqpgTF+SQ3VMEYN1ewLQcN0KroPpphMDXfCFPdR0XToCOyims5RMER6mMo6s2CIUZ1UsysALQVUtC8bhlhLZRthiOx9VFQATTVUtBqGuI3KfgRDrKaiGujK76CicpghpZaK3g/ADOVU1JEPbVVU1OHADIVU9Gcwg9NBRVXQl9lMRXuzYIZ/oZI3YIasvVTUnAkPzKCqaphhRA9VfBdmqKaqGfBCSi1VlcEMK6jgVzBDGVXVpsATY6iq/RoY4ZJ/p1j9t2CEa9qpagw8Uk1VdZkwwrBjFGodDiNk1lFVNbwyJExVLwdghMIuivTcAiMEXqaqtiHwzBwqew5muJcis2GG56jsQXgn+A6VzYUZnqTAczDDXCp7NwgPOV1UVgozlHexl7pnwgylVNZ9LTz1GJV1T4YZxh9jr5yYCDNM7qayx+GtvnuprP0GmGHoHvbC3j+GGW5op7J96fBYQYTKWq6GGbKX9/AiIv90KcxwdQvV/Sk8t5TqGvNgiP/6f3hBmxwYIq+R6pbDezlNVFeXC1OM3c7zqi2EKXLrqO7QtxAFt1HDjgwYY9I/t/IcPn/5L1Jgiowd1HAnomItNWxKhTnSbv55E89w6IU/6wNzpG6ihg2IjoFHqOHFFJgk5U/ufODJVW/urd9a/fTsvxwdgElSXqSGY4MQJZOo42n4eulp6rgdUbOYOh6Er1cepI5liJ5+/04NkRL4eqEkQg31mYiiazupoasEvosq6aKGru8iqh6kjsiD8F3EgxHqqEB0pWyhlqdT4LuAlKep5a0gomzQMWp5MRW+80p9kVpO5CHqbqeeTRnwnUfGJur5EWJgGfXsyIXvnHJ3UM8qxELGv1FPXR5855BXRz2/yUZM/JdW6mm8Gr6zXN1IPZ9/BzEyOUI9LTfA9w03tFDTVMRMJTW1T4bvDJPbqekJxE7gVWrqLoXvNKXd1PRaEDH0rQbqmgvfH8ylrgO5iKlr26nruQB8vxN4jrra/wQxVkJtL2fC91uZL1PbjxFzz1Nb3TXw4Zo6aluM2Et7m9ray5D0ytqp7e00xMGABuqrzkJSy6qmvoYBiIvhLdS310ESc/ZSX8twxMn4k9TXUY6kVd5BfSfHI26K6YXV2UhK2avphWLE0cP0wr5RSEKj9tELDyOuVtILnfch6dzXSS+sRHylvUFP1OQgqeTU0BNvpCHOcvbQE/tHI4mM3k9P7MlB3OUfpidOzkLSmHWSnjicDwOMaqU31g9CUhi0nt5oHQUjXB+mNz6bHULCC83+jN4IXw9D3NRBj+wegwQ3Zjc90nETjDG5ix6JLMtFAstdFqFHuibDIEU99Mqxu1OQoFLuPkav9BTBKKURemb7tUhI126nZyKlMMwseqf72WwknOxnu+mdWTBOBT3UXIQEU9RMD1XAQI/TS1uuRAK5cgu99DiM9Di91FmZjgSRXtlJLz0OQ1XQUw2TkBAmNdBTFTDWrAg9te1WWO/WbfRUZBYMVtpDb+2aGoDFAlN30Vs9pTBaURc9VlcSgqVCJXX0WFcRDDe5g15rKO8DC/Upb6DXOibDeDeF6bmm2RmwTMbsJnoufBMscH0rvXe0IgcWyak4Su+1Xg8rjDrMKGit6g9L9K9qZRQcHgVL5O9hNIQXDoIFBi0MMxr+PR/WyHmDUdG5ZDgMN3xJJ6PizUthkbSVjJK3itNhrPTitxglq9Jgl4cZLScWOTCSs+gEo+XRFNim+CSjprYsC4bJKqtl1HT9BBYa38Lo+WL5hBCMEZqw/AtGT+sEWGl4A6PpyJLCIAwQLFxyhNF08GpYasDbjK5Di8YGEFeBsYsOMbre+SNYK+15RlvjM+NSESep455pZLQt7wubzWhn1LWuK8tDzA0pW9fKqOv8K1hu5MeMhT0LJvRFzPSdsGAPY+HT78F6l73K2GjbOMdNRdSlunM2tjE23vhPSACBqghjpW1r5S05iJqcWyq3tjFmng4hMfx5K2Oo5/3F064IwGOBK6Ytfr+HMfTFHUgYf7yHMRbeuWxmYX94on/hzGU7w4yxvVchgWT+kvHQvPmpuwryglAUzCu466nNzYyHF7ORWKYeZ7x0ffTa0oeK3AFB9FJwgFv00NLXPupivJwoQsIZ/DrjLHK8fvsrKxdWlN8xcdx1I68aNmRATr9gsF/OgCHDrhp53biJd5RXLFz5yvb64xHG2dYhSECBvz1JXy+c/LsAEtPIOvou6jd/goTV7+f0XcSSfkhkk47QdwFHJiPBDVxL33mtHYjEd1sTfefUdBuSQs7SCH1niSzNQbIo2EvfN+wtQBLp+1gXfafpeqwvkovzDn1/8I6DpBOcE6bvd8JzgkhGQ6rp+63qIUhWY2qZ9GrHIImlzGhmUmuekYLkllnVwaTVUZUJX34Nk1RNPnynFOxiEtpVAN/vBabvY5LZNz0A39dCP/mYSeRAaQi+M6WVf8ok0XhvGnxn63N/M5PA4Qf6wndu/eYcYYI79tMM+M4v86GjTGDH52XDd2H97q1ngtp/XwZ8Fxe47W0moO1TgvD10vfX9jCh9Ky7Hj6JYc+3MWG0Lb4CPqnceYeZEA7Py4VPReqUzRFaLrJ5Sip8yvIfbaTFGh/Nh09PcNKGblqpe8OkIHweGDzvAK1zYN5g+LwSuPmlz2mRz1+6OQCfp9JvXx2mFcKrb0+HLwoy7lzbTsO1r70zA76oyZq2vpPG6lw/LQu+KLukpKaVBmqtKbkEvpgIja16L0KDRN6rGhuCL5YGllQfoxGOVZcMhC8OAu78HT2Mq54d890AfPGTPfGR18OMi/Drj0zMhi/+QqNnrWlmTDWvmTU6BJ9Bhha/8EEXY6DrgxeKh8JnorQR05/Y1MSoadr05PQRafCZ7bLx9/9iZ5ieaqtdNrMwFz5rBPLGzXh4xdZPeqil5+CvV87/ccG3A/DZKXXojaWVq7bsOthGgbaDu7asqiy9cWgafIkiffCIwqn3zF2wYs2GV9/c9u6H9QcOtYS7u8Mthw7Uf/jutjdf3bBmxYK590wtHDE4HUnj/wPmFU57dvLWiwAAAABJRU5ErkJggg==';
        } else if (
            copilotReply.includes('Kudos') ||
            copilotReply.includes('Nice work!') ||
            copilotReply.includes('Great job') ||
            copilotReply.includes('Nicely done!')
        ) {
            title = 'Agent Praise';
            icon =
                'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik01LjAwMDAzIDcuNUM1LjAwMDAzIDguODI2MDggNS41MjY4MSAxMC4wOTc5IDYuNDY0NSAxMS4wMzU1QzcuNDAyMTggMTEuOTczMiA4LjY3Mzk1IDEyLjUgMTAgMTIuNUMxMS4zMjYxIDEyLjUgMTIuNTk3OSAxMS45NzMyIDEzLjUzNTYgMTEuMDM1NUMxNC40NzMyIDEwLjA5NzkgMTUgOC44MjYwOCAxNSA3LjVDMTUgNi4xNzM5MiAxNC40NzMyIDQuOTAyMTUgMTMuNTM1NiAzLjk2NDQ3QzEyLjU5NzkgMy4wMjY3OCAxMS4zMjYxIDIuNSAxMCAyLjVDOC42NzM5NSAyLjUgNy40MDIxOCAzLjAyNjc4IDYuNDY0NSAzLjk2NDQ3QzUuNTI2ODEgNC45MDIxNSA1LjAwMDAzIDYuMTczOTIgNS4wMDAwMyA3LjVaIiBzdHJva2U9IiMwMDg5NDAiIHN0cm9rZS13aWR0aD0iMS42NjY2NyIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+DQo8cGF0aCBkPSJNMTAgMTIuNUwxMi44MzM0IDE3LjQwODNMMTQuMTY1IDE0LjcxNDJMMTcuMTYzNCAxNC45MDc1TDE0LjMzIDEwTTUuNjY4MzYgMTBMMi44MzUwMiAxNC45MDgzTDUuODMzMzYgMTQuNzE0Mkw3LjE2NTAyIDE3LjQwNzVMOS45OTgzNiAxMi41IiBzdHJva2U9IiMwMDg5NDAiIHN0cm9rZS13aWR0aD0iMS42NjY2NyIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+DQo8L3N2Zz4NCg==';
        } else {
            title = 'Copilot Insight';
            icon =
                'data:image/svg+xml;base64,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';
        }

        const responseContent = {
            title: title,
            icon: icon,
            copilotReply:
                (copilotReply && typeof copilotReply === 'string'
                    ? copilotReply?.includes(':')
                        ? copilotReply
                            ?.replace(/<\/?[^>]+(>|$)/g, '')
                            ?.split(':')
                            ?.slice(1)
                            ?.join(':')
                            ?.trim()
                        : copilotReply?.replace(/<\/?[^>]+(>|$)/g, '')?.trim()
                    : '') || '',
        };

        return responseContent;
    };

    /**
     * Function to handle user reply
     * @example - handleIntent()
     * @returns
     */
    const handleIntent = (userReply: string) => {
        const intentTitle = intents[0]?.title ?? 'no matching intent title found';
        const intentText = intents[0]?.intent ?? 'no matching intent found';
        const tasks = intents[0]?.tasks ?? [];
        const outputPayload = {
            intent: intentText,
            title: intentTitle,
            tasks: [],
        };

        const currentDate = new Date().toLocaleDateString();
        const modifiedTasks = tasks?.map((task: any) => {
            const taskData = {
                order: task.order,
                text: task.text.replace('%%date%%', currentDate),
            };
            if (task.buttons) {
                const choices = task.buttons.map((button: any) => {
                    return { title: button.text, value: button.text };
                });
                return { ...taskData, choices };
            }
            return taskData;
        });

        // Assign modified tasks array to output payload
        outputPayload.tasks = modifiedTasks;

        const intentToBeDisplayed = `It looks like ${activeContact?.customer?.firstName?.split(' ')[0]
            } would like to ${intentTitle}.`;
        const filteredTasks = outputPayload?.tasks?.filter(
            (task: any) => task.choices
        );
        filteredTasks.forEach((task: any, index: any) => {
            task.changedOrder = index + 1;
        });
        const responseContent = {
            title: `Task Assist: ${intentTitle}`,
            intent: intentToBeDisplayed,
            intentSubTitle: intentTitle,
            tasks: filteredTasks,
        };
        const matchPercentage = calculateStringMatchPercentage(userReply, intentText);
        console.group('handleIntent');
        console.log('%c intentText ', 'color: red', intentText);
        console.log('%c userReply ', 'color: blue', userReply);
        console.log('match % -->', matchPercentage);
        console.groupEnd();
        if (matchPercentage >= UTTERANCE_MATCH_THRESHOLD) {
            const response = {
                agentAssistSource: 'Illuminate',
                contactId: `${activeContact?.id}`,
                utcTimestamp: new Date().toISOString(), // '2024-04-23T04:27:23.455584Z'
                acpAppElements: [
                    {
                        cardType: 'AdaptiveCard',
                        contentType: 'intentAndAssistance',
                        content: JSON.stringify(responseContent),
                        objectId: `e15d4e75-39fa-4f9d-85a1-daffe6d16a40_intent_${intentText}`,
                    },
                ],
                type: null,
            } as ApiResponse;
            processApiResponse(response, dispatch);
            return;
        }
    };

    /**
     * Function to handle user reply
     * @example - handleBehavioralGuidance()
     * @returns
     */
    const handleBehavioralGuidance = (userReply: string) => {
        for (const customReply of customReplies) {
            const statement = customReply.statement;
            const copilotReply = customReply.copilotReply;

            const matchPercentage = calculateStringMatchPercentage(userReply, statement);
            // console.group('handleBehavioralGuidance');
            // console.log('%c statement ', 'color: red', statement);
            // console.log('%c userReply ', 'color: blue', userReply);
            // console.log('match % -->', matchPercentage);
            // console.groupEnd();

            if (matchPercentage >= UTTERANCE_MATCH_THRESHOLD) {
                const responseContent = formatToBehaviouralgudanceCardContent(copilotReply);
                const response = {
                    agentAssistSource: 'Illuminate',
                    contactId: `${activeContact?.id}`,
                    utcTimestamp: new Date().toISOString(),
                    acpAppElements: [
                        {
                            cardType: 'AdaptiveCard',
                            contentType: 'behavioralGuidance',
                            content: JSON.stringify(responseContent),
                            objectId: `e15d4e75-39fa-4f9d-85a1-daffe6d16a40_behavioralGuidance_${statement}`,
                        },
                    ],
                    type: null,
                } as ApiResponse;

                processApiResponse(response, dispatch);
                return;
            }
        }
    };

    /**
     * Formats the sentiment reason and sentiment string into a sentiment card content object.
     *
     * @param sentimentReason - The reason for the sentiment.
     * @param sentimentString - The string indicating the sentiment, which can include 'Neutral', 'Positive', or 'Negative'.
     * @returns An object containing the sentiment, icon, and description based on the provided sentiment string.
     *
     * @example
     * const sentimentCard = formatToSentimentCardContent('Customer is satisfied with the service', 'Positive');
     */
    const formatToSentimentCardContent = (sentimentReason: string, sentimentString: string) => {
        let sentiment, icon;
        if (sentimentString.includes('Neutral')) {
            sentiment = 'Customer Sentiment is Neutral';
            icon = 'NeutralPositiveSentimentIcon';
        } else if (sentimentString.includes('Positive')) {
            sentiment = 'Customer Sentiment is Positive';
            icon = 'PositiveSentimentIcon';
        } else if (sentimentString.includes('Negative')) {
            sentiment = 'Customer Sentiment is Negative';
            icon = 'NegativeSentimentIcon';
        }
        const responseContent = {
            sentiment: sentiment,
            icon: icon,
            description: sentimentReason,
        };
        return responseContent;
    };

    /**
     * Function to handle user reply
     * @example - handleCustomerSentiment()
     * @returns
     */
    const handleCustomerSentiment = (userReply: string) => {
        for (const custSentiment of customerSentiment) {
            const sentimentString = custSentiment.sentiment;
            const statement = custSentiment.statement;
            const reason = custSentiment.reason;
            const matchPercentage = calculateStringMatchPercentage(userReply, statement);
            // console.group('handleCustomerSentiment');
            // console.log('%c statement ', 'color: red', statement);
            // console.log('%c userReply ', 'color: blue', userReply);
            // console.log('match % -->', matchPercentage);
            // console.groupEnd();
            if (matchPercentage >= UTTERANCE_MATCH_THRESHOLD) {
                const responseContent = formatToSentimentCardContent(reason, sentimentString);
                const response = {
                    agentAssistSource: 'Illuminate',
                    contactId: `${activeContact?.id}`,
                    utcTimestamp: new Date().toISOString(),
                    acpAppElements: [
                        {
                            cardType: 'AdaptiveCard',
                            contentType: 'sentimentAndReason',
                            content: JSON.stringify(responseContent),
                            objectId: `e15d4e75-39fa-4f9d-85a1-daffe6d16a40_sentiment_${statement}`,
                        },
                    ],
                    type: null,
                } as ApiResponse;

                processApiResponse(response, dispatch);
                document.getElementById('bodyContainer')?.scroll({
                    top: document.getElementById('bodyContainer')?.scrollHeight, //scroll to the bottom of the element
                    behavior: 'smooth', //auto, smooth, initial, inherit
                });
                return;
            }
        }
    };

  const toggleRecording = () => {
     console.log("MediaRecorder current state :[before]: ",mediaRecorderRef.current?.state);
    if (mediaRecorderRef.current?.state && mediaRecorderRef.current.state !== 'paused') {
      mediaRecorderRef.current?.pause();
    } else {
      setTranscript('');
      mediaRecorderRef.current?.resume();
    }
    console.log("MediaRecorder current state :[after]: ",mediaRecorderRef.current?.state);
    setIsRecording((prevState) => !prevState);
  };

  const sendToProcessUtteranceAPI = (
    customerRequestBody: any,
    audioTranscriptText: string
  ) => {
    if (audioTranscriptText !== '') {
      return new Promise<void>((resolve, reject) => {
        const apiCallPromises = [];
        apiCallPromises.push(
          callApi(
            API_BASE_URL + PROCESS_UTTERANCE,
            'POST',
            customerRequestBody,
            dispatch
          ).catch((error: any) => {
            console.error('Error:', error);
          })
        );
        apiCallPromises.push(
          callApi(
            API_BASE_URL + PROCESS_UTTERANCE,
            'POST',
            { ...customerRequestBody, getKbCombo: true },
            dispatch
          )
            .then((response: any) => {
              callApi(
                API_BASE_URL + PROCESS_UTTERANCE,
                'POST',
                { ...customerRequestBody, getProcessSteps: true },
                dispatch
              ).catch((error: any) => {
                console.error('Error:', error);
              });
            })
            .catch((error: any) => {
              console.error('Error:', error);
            })
        );
        // salesOffers
        // salesOffers API call
        if (copilotConfigurations?.salesOffers ?? true) {
          apiCallPromises.push(
            callApi(
              API_BASE_URL + PROCESS_UTTERANCE,
              'POST',
              { ...customerRequestBody, getSalesOffers: true },
              dispatch
            ).catch((error: any) => {
              console.error('Error:', error);
            })
          );
        }
        // compliance
        // compliance API call
        if (copilotConfigurations?.compliance ?? true) {
          apiCallPromises.push(
            callApi(
              API_BASE_URL + PROCESS_UTTERANCE,
              'POST',
              { ...customerRequestBody, getCompliance: true },
              dispatch
            ).catch((error: any) => {
              console.error('Error:', error);
            })
          );
        }
        // conversation Assistance
        // conversation Assistance API call
        apiCallPromises.push(
          callApi(
            API_BASE_URL + PROCESS_UTTERANCE,
            'POST',
            { ...customerRequestBody, getConversationAssistance: true },
            dispatch
          ).catch((error: any) => {
            console.error('Error:', error);
          })
        );
        // Process all API call promises
        // Promise.allSettled(apiCallPromises).then(() => {
        //   dispatch(setIsRequestProcessing(false));
        // });
      });
    }
    return null;
  };

  useEffect(() => {
    // Establish Deepgram WebSocket connection
    if (!deepgramSocketRef.current) {
      deepgramSocketRef.current = new WebSocket(
        `wss://api.deepgram.com/v1/listen?model=nova-2&language=en-US&smart_format=true&interim_results=false&vad_events=false&callback=https://1tx1doz6q5.execute-api.us-west-2.amazonaws.com/test/agentcopilotapi/v1/transcription/callback/agent?contactId=${activeContact?.id}`,
        ['token', '8c1698cb02286db59314fa30e32fdc00ef668aa3']
      );
    }
    deepgramSocketRef.current.onopen = () => {
      console.log('WebSocket connection established');
      setInterval(() => {
        const keepAliveMsg = JSON.stringify({ type: "KeepAlive" });
        deepgramSocketRef?.current?.send(keepAliveMsg);
    }, 3000);
    };

    deepgramSocketRef.current.onmessage = (message) => {
      try {
        // console.log({ event: 'onmessage', message });
        const received = JSON.parse(message.data);
        const audioTranscriptText =
          received?.channel?.alternatives?.[0]?.transcript || '';
        if (audioTranscriptText.length > 0) {
          console.log('%c Transcript:', 'color:red', audioTranscriptText);
          if(!impersonatePatron) {
              dispatch(setAudioTranscript('Agent:' + audioTranscriptText));
              setTimeout(() => {
                dispatch(setAudioTranscript(''));
              }, 5000);
          }

          const customerRequestBody = {
            ...requestBody,
            messageBody: audioTranscriptText,
            participantId: impersonatePatron ? 'patron' :'agent',
            transcriptionEventId: uuidv5(audioTranscriptText, namespace),
          };
          // Send transcription result to process-utterance API
          sendToProcessUtteranceAPI(customerRequestBody, audioTranscriptText);
        }
      } catch (error) {
        console.log('Error parsing message:', error);
      }
    };

    deepgramSocketRef.current.onclose = () => {
      console.log({ event: 'onclose' });
    };

    deepgramSocketRef.current.onerror = (error) => {
      console.log({ event: 'onerror', error });
    };

    // Only initialize if acpWsClient.current is null
    if (!acpWsClient.current) {
      acpWsClient.current = new LiveVoiceWSClient();
    }

    // Connect to the WebSocket server
    acpWsClient.current.connect(WEBSOCKET_URL);

    // Set up connection status and event handlers
    acpWsClient.current.onOpen = () => {
      setConnectionStatus('Connected');

      acpWsClient.current?.sendMessage(
        JSON.stringify({
          audioPacket: '',
          language: 'en-us',
          type: 'CONNECT',
          contactId: activeContact?.id,
          participantId: 'agent',
        })
      );
    };

    acpWsClient.current.onClosed = () => {
      setConnectionStatus('Disconnected');
      // Attempt reconnection on close
      acpWsClient.current?.attemptReconnect({
        retryOptions: {
          maxRetryAttempts: 3,
          retryInterval: 1000,
        },
        url: WEBSOCKET_URL,
      });
    };

    acpWsClient.current.onError = () => {
      setConnectionStatus('Error');
      // Attempt to reconnect on error
      acpWsClient.current?.attemptReconnect({
        retryOptions: { maxRetryAttempts: 3, retryInterval: 1000 },
        url: WEBSOCKET_URL,
      });
    };

    // Handle incoming messages
    acpWsClient.current.onMessage = (message) => handleAcpWsMessages(message);

    // Cleanup on component unmount
    return () => {
      acpWsClient.current?.onClosed();
    };
  }, []);

  useEffect(() => {
    // Cleanup function to run when the component unmounts
    return () => {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      acpWsClient.current?.onClosed(); // Close the WebSocket connection
    };
  }, []);

  useEffect(() => {
    // const chunks: BlobPart[] = [];
    if (!mediaRecorderRef.current) {
      console.log('MediaRecorder is not initialized');

    // Request audio permission every time the component loads
    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((permissionStatus) => {
        if (permissionStatus.state === 'denied') {
          setError(
            'Microphone access is denied. Please enable it in your browser settings.'
          );
        } else if (permissionStatus.state === 'granted') {
          setError(null);
        } else {
          // Start recording audio
          navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then((stream) => {
              mediaRecorderRef.current = new MediaRecorder(stream);

              mediaRecorderRef.current.ondataavailable = (event) => {
                if (deepgramSocketRef?.current?.readyState === WebSocket.OPEN) {
                  deepgramSocketRef.current.send(event.data);
                }
              };
              mediaRecorderRef.current.start(100); // Send audio data every 100ms
            })
            .catch((error) => {
              console.error('Error accessing media devices.', error);
            });
        }
      });
    //   // Start recording audio
    //   navigator.mediaDevices
    //     .getUserMedia({ audio: true })
    //     .then((stream) => {
    //       mediaRecorderRef.current = new MediaRecorder(stream);

    //       mediaRecorderRef.current.ondataavailable = (event) => {
    //         if (deepgramSocketRef?.current?.readyState === WebSocket.OPEN) {
    //             deepgramSocketRef.current.send(event.data);
    //         }
    //       }
    //       mediaRecorderRef.current.start(100); // Send audio data every 100ms
    //     })
    //     .catch((error) => {
    //       console.error('Error accessing media devices.', error);
    //     });
    } else {
      console.log(
        'MediaRecorder is already initialized, current state:',
        mediaRecorderRef.current?.state
      );
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'ArrowRight') {
        console.log(`${event.code} key pressed`);
        toggleRecording();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    notification.info({
      message: isRecording ? 'Microphone enabled' : 'Microphone disabled',
      placement: 'bottomRight',
      icon: isRecording ? <MicIcon /> : <MicOffIcon />,
      style: {
        backgroundColor: isRecording ? '#4CAF50' : '#f44336',
      }
    });
  }, [isRecording]);

  return (
    <CcfBox>
      <>
        {/* <CcfButton
          style={{
            margin: '5px',
            marginLeft: '8px',
            backgroundColor: isRecording ? '#E6FFE6' : '#F2E3E0',
          }}
          onClick={() => toggleRecording()}
        > */}
          {/* <CcfTypography>{isRecording ? 'Mic On' : 'Mic Off'}</CcfTypography> */}
        {/* {isRecording ? <MicIcon /> : <MicOffIcon />} */}

        {/* </CcfButton> */}
        {/* <CcfButton>
          <CcfTypography>{connectionStatus}</CcfTypography>
        </CcfButton> */}
        {error && (
          <CcfTypography style={{ color: 'red' }}>{error}</CcfTypography>
        )}
        <CcfTypography>{transcript}</CcfTypography>
      </>
    </CcfBox>
  );
}

export default CcfCopilotHca;
